import * as T from 'three';

// Seamless, deterministic material maps. Colour, relief and roughness are separate.
function maps(kind, size = 1024) {
  const colour = document.createElement('canvas'), relief = document.createElement('canvas');
  colour.width = colour.height = relief.width = relief.height = size;
  const cc = colour.getContext('2d'), hc = relief.getContext('2d');
  const pixels = cc.createImageData(size, size), heights = hc.createImageData(size, size);
  let seed = 4099;
  const random = () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const u = x / size, v = y / size, i = (y * size + x) * 4, noise = random() - .5;
    let tone, rgb, height;
    if (kind === 'wood') {
      const wave = v * 14 + .30 * Math.sin(u * Math.PI * 2) + .14 * Math.sin(u * Math.PI * 6 + v * Math.PI * 2);
      const late = Math.pow(.5 + .5 * Math.sin(wave * Math.PI * 2), 12);
      const fibre = Math.pow(.5 + .5 * Math.sin((wave * 5 + .07 * Math.sin(v * 70)) * Math.PI * 2), 18);
      tone = 1 + .065 * Math.sin(wave * Math.PI / 2) - .16 * late - .03 * fibre + noise * .025;
      rgb = [228, 208, 176]; height = 155 - late * 42 - fibre * 18 + noise * 14;
    } else if (kind === 'felt') {
      const cloud = Math.sin(u * 41 + Math.sin(v * 25)) * Math.cos(v * 37 + Math.sin(u * 13));
      tone = .95 + cloud * .05 + noise * .12; rgb = [238, 238, 228];
      height = 140 + cloud * 19 + noise * 100;
    } else {
      // Fine sprayed lacquer: subtle orange peel, not a flat RGB swatch.
      const cloud = Math.sin(u * 53 + Math.cos(v * 43)) * Math.cos(v * 61 + Math.sin(u * 32));
      tone = .97 + cloud * .017 + noise * .045; rgb = [255, 255, 255];
      height = 145 + cloud * 26 + noise * 53;
    }
    for (let c = 0; c < 3; c++) { pixels.data[i + c] = rgb[c] * tone; heights.data[i + c] = height; }
    pixels.data[i + 3] = heights.data[i + 3] = 255;
  }
  cc.putImageData(pixels, 0, 0); hc.putImageData(heights, 0, 0);
  const map = new T.CanvasTexture(colour), bumpMap = new T.CanvasTexture(relief);
  map.colorSpace = T.SRGBColorSpace;
  for (const t of [map, bumpMap]) { t.wrapS = t.wrapT = T.RepeatWrapping; t.anisotropy = 8; }
  return { map, bumpMap };
}
const oak = maps('wood'), paint = maps('paint', 512), felt = maps('felt', 512);
// Shared per colour/roughness so identical parts can be merged into one draw call.
const lacquers = new Map();
export function lacquer(color, roughness = .43) {
  const key = color + '/' + roughness;
  if (!lacquers.has(key)) lacquers.set(key, new T.MeshPhysicalMaterial({ color, ...paint, bumpScale: .007, roughness,
    clearcoat: .38, clearcoatRoughness: .26 }));
  return lacquers.get(key);
}
export const wood = new T.MeshPhysicalMaterial({ color: 0xfffaf2, ...oak, bumpScale: .014,
  roughness: .5, clearcoat: .24, clearcoatRoughness: .4 });
export const walnut = new T.MeshPhysicalMaterial({ color: 0x765238, ...oak, bumpScale: .026,
  roughness: .4, clearcoat: .25, clearcoatRoughness: .35 });
// Soft-touch plastics: a faint velvet sheen and a thin satin coat, like the matte shells in the renders.
export const cream = new T.MeshPhysicalMaterial({ color: 0xf5f0e7, ...paint, bumpScale: .0012, roughness: .5,
  clearcoat: .22, clearcoatRoughness: .42, sheen: .35, sheenRoughness: .7, sheenColor: 0xffffff });
export const dark = new T.MeshStandardMaterial({color:0x353b31,roughness:.83});
export const trayLining = new T.MeshStandardMaterial({ color: 0x777e60, ...felt, bumpScale:.032, roughness:.95 });
// Botanical print redrawn from the September hardware renders; deterministic and local.
function botanicalPrint(){
  const S=2048,canvas=document.createElement('canvas');canvas.width=canvas.height=S;
  const c=canvas.getContext('2d');c.fillStyle='#aebf84';c.fillRect(0,0,S,S);
  let seed=91317;const rnd=()=>((seed=seed*16807%2147483647)-1)/2147483646;
  // Draw near-edge motifs again on the opposite side so the print tiles without seams.
  const wrap=(x,y,r,draw)=>{for(const dx of[-S,0,S])for(const dy of[-S,0,S]){if(x+dx<-r||x+dx>S+r||y+dy<-r||y+dy>S+r)continue;c.save();c.translate(x+dx,y+dy);draw();c.restore();}};
  // Soft mottling like printed paper.
  for(let n=0;n<7000;n++){const x=rnd()*S,y=rnd()*S,r=6+rnd()*30,a=rnd()*6.28;c.fillStyle=['#a9bc7630','#c5d39238','#9fb46c28'][n%3];wrap(x,y,r,()=>{c.beginPath();c.ellipse(0,0,r,r*.75,a,0,6.28);c.fill();});}
  const heart=(s,a,col)=>{c.save();c.rotate(a);c.fillStyle=col;c.beginPath();c.moveTo(0,0);c.bezierCurveTo(-s*.9,-s*.3,-s*.7,-s*1.25,0,-s*.85);c.bezierCurveTo(s*.7,-s*1.25,s*.9,-s*.3,0,0);c.fill();c.restore();};
  const clover=(s,col)=>{c.strokeStyle='#7f9a5b';c.lineWidth=s*.12;c.beginPath();c.moveTo(0,0);c.quadraticCurveTo(s*.2,s*.6,-s*.1,s*1.1);c.stroke();for(let k=0;k<3;k++)heart(s,k*2.094,col);c.fillStyle='#9fb875';c.beginPath();c.arc(0,0,s*.12,0,6.28);c.fill();};
  const daisy=(s)=>{c.fillStyle='#fffdf4';for(let k=0;k<8;k++){c.save();c.rotate(k*.785);c.beginPath();c.ellipse(0,-s*.62,s*.22,s*.5,0,0,6.28);c.fill();c.restore();}c.fillStyle='#f2c24e';c.beginPath();c.arc(0,0,s*.3,0,6.28);c.fill();};
  const blossom=(s,col,mid)=>{c.fillStyle=col;for(let k=0;k<5;k++){c.save();c.rotate(k*1.2566);c.beginPath();c.arc(0,-s*.45,s*.36,0,6.28);c.fill();c.restore();}c.fillStyle=mid;c.beginPath();c.arc(0,0,s*.24,0,6.28);c.fill();};
  const sprig=(s)=>{c.strokeStyle='#87a262';c.lineWidth=s*.1;c.beginPath();c.moveTo(0,s);c.quadraticCurveTo(s*.2,0,0,-s);c.stroke();c.fillStyle='#8aa765';for(const t of[-.5,0,.5]){for(const d of[-1,1]){c.save();c.translate(0,t*s*1.4);c.rotate(d*.9);c.beginPath();c.ellipse(0,-s*.25,s*.13,s*.3,0,0,6.28);c.fill();c.restore();}}};
  // Jittered grid keeps motifs evenly spread with no clumps.
  const scatter=(cell,draw,size)=>{for(let gy=0;gy<S;gy+=cell)for(let gx=0;gx<S;gx+=cell){const x=gx+rnd()*cell,y=gy+rnd()*cell,s=size*(.8+rnd()*.45),a=rnd()*6.28,v=rnd();wrap(x,y,s*1.6,()=>{c.rotate(a);draw(s,v);});}};
  scatter(150,s=>sprig(s),26);
  scatter(128,(s,v)=>clover(s,v<.5?'#86a45e':'#94b16a'),22);
  scatter(205,s=>daisy(s),23);
  scatter(230,s=>blossom(s,'#f6d56b','#e9a93c'),13);
  scatter(260,s=>blossom(s,'#cfe4f0','#f7e9a5'),12);
  const map=new T.CanvasTexture(canvas);map.colorSpace=T.SRGBColorSpace;map.wrapS=map.wrapT=T.RepeatWrapping;map.anisotropy=8;
  return map;
}
export const floral = new T.MeshPhysicalMaterial({color:0xffffff,map:botanicalPrint(),bumpMap:paint.bumpMap,bumpScale:.004,roughness:.62,clearcoat:.18,clearcoatRoughness:.45});
export const shell = new T.MeshPhysicalMaterial({ color: 0x7c8c56, ...paint, bumpScale: .005, roughness: .55,
  clearcoat: .2, clearcoatRoughness: .45, sheen: .4, sheenRoughness: .6, sheenColor: 0xe6efd2 });
